import { useState, useEffect, useCallback, useRef } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { offlineDb } from '@/lib/offline-db';
import { syncAll } from '@/lib/sync-service';

export function useOfflineSync() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const syncingRef = useRef(false);

  const pendingActionCount = useLiveQuery(
    () => offlineDb.pendingActions.count(),
    [],
    0,
  );

  const unsyncedGpsCount = useLiveQuery(
    () => offlineDb.cachedGpsPoints.where('synced').equals(0).count(),
    [],
    0,
  );

  const pendingCount = (pendingActionCount ?? 0) + (unsyncedGpsCount ?? 0);

  const syncNow = useCallback(async () => {
    if (syncingRef.current || !navigator.onLine) return;
    syncingRef.current = true;
    setIsSyncing(true);
    try {
      await syncAll();
    } catch {
      // Sync failed — will retry on next trigger
    } finally {
      syncingRef.current = false;
      setIsSyncing(false);
    }
  }, []);

  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      syncNow();
    };
    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [syncNow]);

  // Auto-sync when coming back online and there are pending items
  useEffect(() => {
    if (isOnline && pendingCount > 0 && !syncingRef.current) {
      syncNow();
    }
  }, [isOnline, pendingCount, syncNow]);

  return { pendingCount, isSyncing, syncNow, isOnline } as const;
}
